import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  Alert,
  Chip,
  Grid
} from '@mui/material';
import {
  LocalShipping as ShippingIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import BackButton from '../../components/common/BackButton';
import { firebaseShippingService, initializeShippingRates } from '../../services/firebaseShippingService';

const AdminShipping = () => {
  const [shippingRates, setShippingRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const emptyFormData = {
    name: '',
    zone: '',
    baseCost: '',
    weightMultiplier: '',
    freeShippingThreshold: '',
    deliveryDays: '',
    active: true,
    alwaysFree: false,
    weightBrackets: [],
    orderValueBrackets: []
  };

  const [formData, setFormData] = useState(emptyFormData);

  // Cargar tarifas de envío
  const loadShippingRates = async () => {
    try {
      setLoading(true);
      const rates = await firebaseShippingService.getAll();
      setShippingRates(rates);
    } catch (error) {
      console.error('Error al cargar tarifas:', error);
      toast.error('Error al cargar las tarifas de envío');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const boot = async () => {
      await initializeShippingRates();
      await loadShippingRates();
    };
    boot();
  }, []);

  // Abrir diálogo para crear/editar
  const openDialog = (rate = null) => {
    if (rate) {
      setEditingRate(rate);
      setFormData({
        name: rate.name || '',
        zone: rate.zone || '',
        baseCost: rate.baseCost ?? '',
        weightMultiplier: rate.weightMultiplier ?? '',
        freeShippingThreshold: rate.freeShippingThreshold ?? '',
        deliveryDays: rate.deliveryDays || '',
        active: rate.active !== false,
        alwaysFree: rate.alwaysFree === true,
        weightBrackets: (rate.weightBrackets || []).map((b) => ({
          maxKg: b.maxKg ?? '',
          cost: b.cost ?? '',
          label: b.label || ''
        })),
        orderValueBrackets: (rate.orderValueBrackets || []).map((b) => ({
          maxAmount: b.maxAmount ?? '',
          cost: b.cost ?? '',
          label: b.label || ''
        }))
      });
    } else {
      setEditingRate(null);
      setFormData(emptyFormData);
    }
    setDialogOpen(true);
  };

  // Cerrar diálogo
  const closeDialog = () => {
    setDialogOpen(false);
    setEditingRate(null);
    setFormData(emptyFormData);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const updateBracket = (type, index, field, value) => {
    const key = type === 'weight' ? 'weightBrackets' : 'orderValueBrackets';
    setFormData((prev) => ({
      ...prev,
      [key]: prev[key].map((row, i) => (i === index ? { ...row, [field]: value } : row))
    }));
  };

  const addBracket = (type) => {
    const key = type === 'weight' ? 'weightBrackets' : 'orderValueBrackets';
    const emptyRow =
      type === 'weight'
        ? { maxKg: '', cost: '', label: '' }
        : { maxAmount: '', cost: '', label: '' };
    setFormData((prev) => ({
      ...prev,
      [key]: [...prev[key], emptyRow]
    }));
  };

  const removeBracket = (type, index) => {
    const key = type === 'weight' ? 'weightBrackets' : 'orderValueBrackets';
    setFormData((prev) => ({
      ...prev,
      [key]: prev[key].filter((_, i) => i !== index)
    }));
  };

  const parseBrackets = (brackets, amountKey) =>
    brackets
      .filter((b) => b.cost !== '' && b.cost != null && !Number.isNaN(parseFloat(b.cost)))
      .map((b) => ({
        [amountKey]: b[amountKey] === '' || b[amountKey] == null ? null : parseFloat(b[amountKey]),
        cost: parseFloat(b.cost),
        label: b.label?.trim() || ''
      }));

  // Guardar tarifa
  const handleSave = async () => {
    try {
      // Validaciones
      if (!formData.name.trim()) {
        toast.error('El nombre es obligatorio');
        return;
      }
      if (!formData.zone.trim()) {
        toast.error('La zona es obligatoria');
        return;
      }
      if (!formData.baseCost || isNaN(formData.baseCost) || formData.baseCost < 0) {
        toast.error('El costo base debe ser un número válido');
        return;
      }

      const rateData = {
        ...formData,
        baseCost: parseFloat(formData.baseCost),
        weightMultiplier: parseFloat(formData.weightMultiplier) || 0,
        freeShippingThreshold: parseFloat(formData.freeShippingThreshold) || null,
        zone: formData.zone.toLowerCase().replace(/\s+/g, '_'),
        alwaysFree: formData.alwaysFree === true,
        weightBrackets: parseBrackets(formData.weightBrackets, 'maxKg'),
        orderValueBrackets: parseBrackets(formData.orderValueBrackets, 'maxAmount')
      };

      if (editingRate) {
        rateData.id = editingRate.id;
      }

      await firebaseShippingService.createOrUpdate(rateData);
      
      toast.success(editingRate ? 'Tarifa actualizada' : 'Tarifa creada');
      closeDialog();
      loadShippingRates();
    } catch (error) {
      console.error('Error al guardar tarifa:', error);
      toast.error('Error al guardar la tarifa');
    }
  };

  // Eliminar tarifa
  const handleDelete = async (rate) => {
    if (window.confirm(`¿Estás seguro de eliminar la tarifa "${rate.name}"?`)) {
      try {
        await firebaseShippingService.delete(rate.id);
        toast.success('Tarifa eliminada');
        loadShippingRates();
      } catch (error) {
        console.error('Error al eliminar tarifa:', error);
        toast.error('Error al eliminar la tarifa');
      }
    }
  };

  // Alternar estado activo
  const toggleActive = async (rate) => {
    try {
      const updatedRate = { ...rate, active: !rate.active };
      await firebaseShippingService.createOrUpdate(updatedRate);
      toast.success(`Tarifa ${updatedRate.active ? 'activada' : 'desactivada'}`);
      loadShippingRates();
    } catch (error) {
      console.error('Error al cambiar estado:', error);
      toast.error('Error al cambiar el estado');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <BackButton to="/admin" />
      
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            mb: 2,
            fontFamily: '"Playfair Display", serif',
            fontWeight: 600,
            color: 'secondary.main'
          }}
        >
          <ShippingIcon sx={{ fontSize: 40 }} />
          Gestión de Envíos
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Configura las tarifas de envío para diferentes zonas geográficas
        </Typography>
      </Box>

      {/* Estadísticas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
            <CardContent>
              <Typography variant="h6">Total Tarifas</Typography>
              <Typography variant="h3">{shippingRates.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'success.main', color: 'white' }}>
            <CardContent>
              <Typography variant="h6">Tarifas Activas</Typography>
              <Typography variant="h3">
                {shippingRates.filter(r => r.active).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'info.main', color: 'white' }}>
            <CardContent>
              <Typography variant="h6">Envío Gratis Mín.</Typography>
              <Typography variant="h3">
                €{shippingRates.length > 0 ? Math.min(...shippingRates.map(r => r.freeShippingThreshold || 999)) : 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Botón Crear */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => openDialog()}
          sx={{ mb: 2 }}
        >
          Nueva Tarifa de Envío
        </Button>
      </Box>

      {/* Tabla de tarifas */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Nombre</strong></TableCell>
              <TableCell><strong>Zona</strong></TableCell>
              <TableCell><strong>Costo Base</strong></TableCell>
              <TableCell><strong>Tramos</strong></TableCell>
              <TableCell><strong>Por Peso (legacy)</strong></TableCell>
              <TableCell><strong>Envío Gratis</strong></TableCell>
              <TableCell><strong>Tiempo</strong></TableCell>
              <TableCell><strong>Estado</strong></TableCell>
              <TableCell><strong>Acciones</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  Cargando tarifas...
                </TableCell>
              </TableRow>
            ) : shippingRates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No hay tarifas configuradas
                </TableCell>
              </TableRow>
            ) : (
              shippingRates.map((rate) => (
                <TableRow key={rate.id}>
                  <TableCell>{rate.name}</TableCell>
                  <TableCell>
                    <Chip 
                      label={rate.zone} 
                      size="small" 
                      color="primary" 
                      variant="outlined" 
                    />
                  </TableCell>
                  <TableCell>€{rate.baseCost?.toFixed(2)}</TableCell>
                  <TableCell>
                    {(rate.weightBrackets?.length || 0) + (rate.orderValueBrackets?.length || 0) > 0
                      ? `${rate.weightBrackets?.length || 0} kg / ${rate.orderValueBrackets?.length || 0} €`
                      : 'Legacy'}
                  </TableCell>
                  <TableCell>
                    {rate.weightMultiplier ? `+€${rate.weightMultiplier}/kg` : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {rate.freeShippingThreshold ? 
                      `€${rate.freeShippingThreshold}+` : 
                      'No disponible'
                    }
                  </TableCell>
                  <TableCell>{rate.deliveryDays || 'N/A'}</TableCell>
                  <TableCell>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={rate.active}
                          onChange={() => toggleActive(rate)}
                          size="small"
                        />
                      }
                      label={rate.active ? 'Activa' : 'Inactiva'}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton 
                      onClick={() => openDialog(rate)}
                      color="primary"
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleDelete(rate)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Diálogo de crear/editar */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRate ? 'Editar Tarifa de Envío' : 'Nueva Tarifa de Envío'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nombre de la Tarifa"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="España Peninsular"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Zona"
                  value={formData.zone}
                  onChange={(e) => handleInputChange('zone', e.target.value)}
                  placeholder="huelva, andalucia, peninsula, balearic, canary"
                  helperText="Tarifas por peso definidas en código según tabla transportista"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Costo Base (€)"
                  type="number"
                  value={formData.baseCost}
                  onChange={(e) => handleInputChange('baseCost', e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Multiplicador por Peso (€/kg)"
                  type="number"
                  value={formData.weightMultiplier}
                  onChange={(e) => handleInputChange('weightMultiplier', e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Costo adicional por kilogramo"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Envío Gratis a partir de (€)"
                  type="number"
                  value={formData.freeShippingThreshold}
                  onChange={(e) => handleInputChange('freeShippingThreshold', e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Monto mínimo para envío gratuito"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Tiempo de Entrega"
                  value={formData.deliveryDays}
                  onChange={(e) => handleInputChange('deliveryDays', e.target.value)}
                  placeholder="2-3 días laborables"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.alwaysFree}
                      onChange={(e) => handleInputChange('alwaysFree', e.target.checked)}
                    />
                  }
                  label="Envío siempre gratuito (ej. Huelva)"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.active}
                      onChange={(e) => handleInputChange('active', e.target.checked)}
                    />
                  }
                  label="Tarifa Activa"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mt: 1, mb: 1, fontWeight: 600 }}>
                  Tramos por peso (kg)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Ejemplo: hasta 5 kg → 4,95 €. Deja &quot;Hasta kg&quot; vacío en el último tramo para el resto.
                </Typography>
                {formData.weightBrackets.map((bracket, index) => (
                  <Grid container spacing={2} key={`w-${index}`} sx={{ mb: 1 }}>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Hasta (kg)"
                        type="number"
                        value={bracket.maxKg}
                        onChange={(e) => updateBracket('weight', index, 'maxKg', e.target.value)}
                        inputProps={{ min: 0, step: 0.1 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Coste (€)"
                        type="number"
                        value={bracket.cost}
                        onChange={(e) => updateBracket('weight', index, 'cost', e.target.value)}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Etiqueta"
                        value={bracket.label}
                        onChange={(e) => updateBracket('weight', index, 'label', e.target.value)}
                        placeholder="Hasta 5 kg"
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <IconButton color="error" onClick={() => removeBracket('weight', index)}>
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
                <Button size="small" startIcon={<AddIcon />} onClick={() => addBracket('weight')}>
                  Añadir tramo de peso
                </Button>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                  Tramos por importe del pedido (€)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Opcional. Si no hay tramos de peso, se usa el importe del pedido (hasta X €).
                </Typography>
                {formData.orderValueBrackets.map((bracket, index) => (
                  <Grid container spacing={2} key={`v-${index}`} sx={{ mb: 1 }}>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Hasta (€)"
                        type="number"
                        value={bracket.maxAmount}
                        onChange={(e) => updateBracket('value', index, 'maxAmount', e.target.value)}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Coste envío (€)"
                        type="number"
                        value={bracket.cost}
                        onChange={(e) => updateBracket('value', index, 'cost', e.target.value)}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Etiqueta"
                        value={bracket.label}
                        onChange={(e) => updateBracket('value', index, 'label', e.target.value)}
                        placeholder="Hasta 50 €"
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <IconButton color="error" onClick={() => removeBracket('value', index)}>
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
                <Button size="small" startIcon={<AddIcon />} onClick={() => addBracket('value')}>
                  Añadir tramo de importe
                </Button>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} startIcon={<CancelIcon />}>
            Cancelar
          </Button>
          <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />}>
            {editingRate ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Información adicional */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Consejos:</strong>
          <br />• Por CP: <strong>huelva</strong> (urbano), <strong>andalucia</strong> (regional),{' '}
          <strong>peninsula</strong> / islas (tarifa peninsular)
          <br />• Tramos de peso: hasta 2 kg, hasta 5 kg, hasta 10 kg, y suplemento €/kg por encima de 10 kg
          <br />• Peso por defecto: 1 kg por botella (editable en cada vino)
        </Typography>
      </Alert>
    </Container>
  );
};

export default AdminShipping; 